import ours from "@oursprivacy/cdp-sdk";

import { isDev, isLocal } from "./config/env";
import { splitTraits } from "./ours.shared";
import type { OursClient } from "./ours.types";
import { captureException } from "./sentry";

type OursWebSdk = Pick<typeof ours, "identify" | "init" | "reset" | "track">;

const initOurs = async (
  sdk: OursWebSdk = ours,
  token = process.env.EXPO_PUBLIC_OURS_WEB_TOKEN,
): Promise<OursClient | null> => {
  if (!token) {
    if (isLocal || isDev) {
      console.warn("Ours web token not set, skipping initialization");
    }
    return null;
  }

  try {
    sdk.init(token, {
      track_web_events: true,
      custom_domain: "https://metrics.curaihealth.com",
      session_replay: {
        token: "replay_sw0y7zzmq8yfqy35asql3jer",
        mask_all_text: true,
      },
    });
  } catch (error) {
    captureException(error);
    return null;
  }

  let externalId: string | undefined;

  return {
    track: (event, props) =>
      sdk.track(
        event,
        props,
        externalId ? { external_id: externalId } : undefined,
      ),
    // The web pixel auto-tracks page_view via track_web_events, so screen() is a no-op.
    screen: () => {},
    identify: (nextExternalId, traits = {}) => {
      externalId = nextExternalId;
      const { email, firstName, lastName, phone, state, custom } =
        splitTraits(traits);
      sdk.identify({
        ...(nextExternalId ? { external_id: nextExternalId } : {}),
        ...(email ? { email } : {}),
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
        ...(phone ? { phone_number: phone } : {}),
        ...(state ? { state } : {}),
        custom_properties: custom,
      });
    },
    reset: () => {
      externalId = undefined;
      sdk.reset();
    },
  };
};

export default initOurs;
